import { Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconClipboardCheck, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import {
  MRT_ColumnDef,
  MRT_TableOptions,
} from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useContractsQueries } from '../../lib/queries/contracts';
import { Batch } from '../../lib/types/batches';

import {
  filterOrganizationColumns,
  Permission,
  usePermissions,
} from '@auth/data-access';
import { FORM_STATUS } from '@data-access';
import {
  formatOrganizationNames,
  OrganizationsList,
  PfTable,
  StatusCell,
  StatusColumnFilter,
  TsrButtonLink,
} from '@ui';

interface SeedBatchesTableProps {
  contractId: number;
}

function formatPratiqueIds(
  pk: number,
  oleozeId: number | null | undefined,
): string {
  return `${pk} / ${oleozeId ?? '—'}`;
}

interface SaipolPratiqueSubRow {
  id: string;
  pratique_pk: number;
  oleoze_id: number | null;
  product_campaign: string;
  organization: { id: number; name: string } | null;
  owner: { id: number; name: string } | null;
  campaign_year: number;
  practice_status: string | null;
  has_data: boolean;
  has_fields: boolean;
  volume_reel: number | null;
  rdt_reel: number | null;
  area_reel: number | null;
}

type SeedBatchParentRow = Batch & {
  subRows: SaipolPratiqueSubRow[];
};

type SeedBatchRow = SeedBatchParentRow | SaipolPratiqueSubRow;

function isSaipolSubRow(row: SeedBatchRow): row is SaipolPratiqueSubRow {
  return (
    typeof row !== 'undefined' &&
    'pratique_pk' in row &&
    typeof (row as SaipolPratiqueSubRow).pratique_pk === 'number'
  );
}

const PRACTICE_STATUS_COLORS: Record<string, string> = {
  annule: 'black',
  'a contractualiser': 'blue',
  'a lotir': 'orange',
  clos: 'green',
};

function practiceStatusColor(status: string | null | undefined): string | undefined {
  if (!status) return undefined;
  const key = status
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return PRACTICE_STATUS_COLORS[key];
}

function formatAreaHa(value?: number | null): string | undefined {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return undefined;
  }
  return value.toFixed(2).replace('.', ',').concat(' ha');
}

function formatVolumeT(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  return value.toFixed(2).replace('.', ',').concat(' t');
}

function formatYieldTonnesPerHa(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  return value.toFixed(2).replace('.', ',').concat(' t/ha');
}

const numericMetricDefaults = {
  grow: false,
  minSize: 72,
  size: 88,
} as const;

const metricColumnLayout = {
  ...numericMetricDefaults,
  mantineTableBodyCellProps: {
    style: { whiteSpace: 'nowrap' as const },
  },
  mantineTableHeadCellProps: {
    style: { whiteSpace: 'nowrap' as const },
  },
} as const;

const PratiqueDataFieldsStatus: FC<{
  hasData: boolean;
  hasFields: boolean;
}> = ({ hasData, hasFields }) => {
  const { t } = useTranslation();

  const block = (label: string, ok: boolean) => (
    <Group align="center" gap={6} wrap="nowrap">
      <Text c="dimmed" component="span" fw={500} size="xs">
        {label}
      </Text>
      <Group align="center" gap={4} wrap="nowrap">
        {ok ? (
          <IconCheck color="green" size={14} />
        ) : (
          <IconX color="red" size={14} />
        )}
        <Text
          component="span"
          c={ok ? 'pf-green.8' : 'red.7'}
          fw={600}
          size="xs"
        >
          {ok ? t('common.yes') : t('common.no')}
        </Text>
      </Group>
    </Group>
  );

  return (
    <Stack align="flex-start" gap={4} justify="flex-start">
      {block(t('pratiques.columns.hasData'), hasData)}
      {block(t('pratiques.columns.hasFields'), hasFields)}
    </Stack>
  );
};

export const SeedBatchesTable: FC<SeedBatchesTableProps> = ({ contractId }) => {
  const { t } = useTranslation();
  const { can, showOrganizationColumn } = usePermissions();
  const showOrgColumn = showOrganizationColumn('list');
  const contractsQueries = useContractsQueries();
  const canEdit = can(Permission.ManageContracts);

  const batches = useQuery(contractsQueries.batchesList({ contractId }));

  const rows = useMemo<SeedBatchRow[]>(() => {
    return (
      batches.data?.map(
        (batch): SeedBatchParentRow => ({
          ...batch,
          subRows: (batch.saipol_pratiques ?? []).map(
            (p): SaipolPratiqueSubRow => ({
              id: `${batch.id}-p-${p.pratique_pk}`,
              pratique_pk: p.pratique_pk,
              oleoze_id: p.oleoze_id,
              product_campaign: p.product_campaign,
              organization: p.organization
                ? { id: p.organization.id, name: p.organization.name }
                : null,
              owner: p.owner
                ? { id: p.owner.id, name: p.owner.name }
                : null,
              campaign_year: p.campaign_year,
              practice_status: p.practice_status,
              has_data: p.has_data,
              has_fields: p.has_fields,
              volume_reel: p.volume_reel,
              rdt_reel: p.rdt_reel,
              area_reel: p.area_reel,
            }),
          ),
        }),
      ) ?? []
    );
  }, [batches.data]);

  const columns = useMemo<MRT_ColumnDef<SeedBatchRow>[]>(
    () =>
      filterOrganizationColumns(
        [
      {
        id: 'code',
        accessorFn: (row) =>
          isSaipolSubRow(row)
            ? formatPratiqueIds(row.pratique_pk, row.oleoze_id)
            : (row.code ?? ''),
        grow: false,
        header: t('pratiques.columns.precifieldOleozeId'),
        minSize: 176,
        size: 200,
        Cell: ({ row, cell }) => {
          if (isSaipolSubRow(row.original)) {
            const practiceStatus = row.original.practice_status;
            const statusColor = practiceStatusColor(practiceStatus);

            return (
              <Group gap={6} wrap="nowrap">
                {statusColor ? (
                  <Box
                    bg={statusColor}
                    h={8}
                    style={{ borderRadius: 999, flexShrink: 0 }}
                    w={8}
                  />
                ) : null}
                <Text
                  component="span"
                  lineClamp={1}
                  size="xs"
                  title={practiceStatus || undefined}
                >
                  {cell.getValue<string>()}
                </Text>
              </Group>
            );
          }

          const code = row.original.code ?? '-';
          const batchId = row.original.id.toString();
          const contractIdStr = String(contractId);
          const label = t('contracts.confirm-batches-fields');

          return (
            <Group align="center" gap={6} justify="flex-start" wrap="nowrap">
              <Text
                component="span"
                lineClamp={1}
                size="xs"
                style={{ flex: '1 1 auto', minWidth: 0 }}
                title={code !== '-' ? code : undefined}
              >
                {code}
              </Text>
              <Tooltip label={label} withArrow>
                <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                  <TsrButtonLink
                    aria-label={label}
                    disabled={!canEdit}
                    params={{
                      batchId,
                      contractId: contractIdStr,
                    }}
                    px={6}
                    size="compact-xs"
                    style={{ alignSelf: 'center' }}
                    to="/os/contracts/$contractId/batches/$batchId"
                    variant="light"
                  >
                    <IconClipboardCheck size={16} stroke={1.5} />
                  </TsrButtonLink>
                </span>
              </Tooltip>
            </Group>
          );
        },
      },
      {
        id: 'crop',
        accessorFn: (row) =>
          isSaipolSubRow(row)
            ? row.product_campaign
            : row.species?.name ?? '',
        grow: true,
        header: t('common.crop'),
        minSize: 140,
        size: 260,
        Cell: ({ cell }) => {
          const v = cell.getValue<string>();
          const text = v?.length ? v : '-';

          return (
            <Text
              component="span"
              lineClamp={3}
              size="sm"
              style={{ wordBreak: 'break-word', width: '100%' }}
              title={typeof text === 'string' ? text : undefined}
            >
              {text}
            </Text>
          );
        },
      },
      {
        id: 'organization',
        accessorFn: (row) =>
          isSaipolSubRow(row)
            ? formatOrganizationNames(
                row.organization ? [row.organization] : null,
              )
            : '',
        grow: false,
        header: t('common.properties.organization'),
        minSize: 120,
        size: 150,
        Cell: ({ row }) => {
          if (!isSaipolSubRow(row.original) || !row.original.organization) {
            return '-';
          }

          return (
            <OrganizationsList organizations={[row.original.organization]} />
          );
        },
      },
      {
        id: 'owner',
        accessorFn: (row) =>
          isSaipolSubRow(row)
            ? formatOrganizationNames(row.owner ? [row.owner] : null)
            : '',
        grow: false,
        header: t('common.properties.owner'),
        minSize: 100,
        size: 120,
        Cell: ({ row }) => {
          if (!isSaipolSubRow(row.original) || !row.original.owner) {
            return '-';
          }

          return <OrganizationsList organizations={[row.original.owner]} />;
        },
      },
      {
        id: 'campaign',
        accessorFn: (row) =>
          isSaipolSubRow(row)
            ? row.campaign_year
            : row.campaign?.year ?? row.campaign?.name ?? '',
        grow: false,
        header: t('common.annee-de-recolte'),
        mantineTableBodyCellProps: {
          style: { whiteSpace: 'nowrap' as const },
        },
        mantineTableHeadCellProps: {
          style: { whiteSpace: 'nowrap' as const },
        },
        maxSize: 96,
        minSize: 52,
        size: 64,
        Cell: ({ row, cell }) => {
          const v = cell.getValue<string | number>();

          return v !== null && v !== undefined && v !== ''
            ? String(v)
            : '-';
        },
      },
      {
        id: 'tonnage',
        ...metricColumnLayout,
        accessorFn: (row) =>
          isSaipolSubRow(row) ? row.volume_reel : row.tonnage,
        header: t('contracts.batches-column-volume'),
        Cell: ({ row, cell }) => {
          if (isSaipolSubRow(row.original)) {
            return formatVolumeT(cell.getValue<number | null | undefined>());
          }

          const v = cell.getValue<number>();

          return formatVolumeT(v);
        },
      },
      {
        id: 'real_yield',
        ...metricColumnLayout,
        accessorFn: (row) =>
          isSaipolSubRow(row) ? row.rdt_reel : (row.real_yield ?? null),
        header: t('contracts.batches-column-yield'),
        Cell: ({ row, cell }) =>
          formatYieldTonnesPerHa(
            cell.getValue<number | null | undefined>(),
          ),
      },
      {
        id: 'area',
        ...metricColumnLayout,
        accessorFn: (row) =>
          isSaipolSubRow(row) ? row.area_reel : row.area,
        enableEditing: false,
        header: t('contracts.batches-column-area'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        Cell: ({ row, cell }) => {
          const val = cell.getValue<number | null | undefined>();

          return formatAreaHa(val ?? undefined) ?? '-';
        },
      },
      {
        id: 'identified_area',
        ...metricColumnLayout,
        accessorFn: (row) =>
          isSaipolSubRow(row) ? undefined : (row.identified_area ?? null),
        enableEditing: false,
        header: t('contracts.batches-column-identified-area'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        Cell: ({ row, cell }) => {
          if (isSaipolSubRow(row.original)) {
            return '-';
          }

          const val = cell.getValue<number | null | undefined>();

          return formatAreaHa(val ?? undefined) ?? '-';
        },
      },
      {
        id: 'status',
        accessorFn: (row) =>
          isSaipolSubRow(row) ? null : row.status,
        grow: true,
        header: t('common.parcelles-saisies'),
        minSize: 200,
        size: 240,
        Cell: ({ row, cell }) => {
          if (isSaipolSubRow(row.original)) {
            const { has_data: hasData, has_fields: hasFields } =
              row.original;

            return (
              <PratiqueDataFieldsStatus
                hasData={hasData}
                hasFields={hasFields}
              />
            );
          }

          const status = cell.getValue<FORM_STATUS>();

          if (!status) {
            return '-';
          }

          return <StatusCell status={status} />;
        },
        Filter: ({ column }) => (
          <StatusColumnFilter
            onChange={(value) => column.setFilterValue(value)}
            onClear={() => column.setFilterValue(null)}
            value={column.getFilterValue() as FORM_STATUS}
          />
        ),
      },
        ],
        showOrgColumn,
      ),
    [t, canEdit, contractId, showOrgColumn],
  );

  const columnOrder = [
    'mrt-row-expand',
    'code',
    'crop',
    ...(showOrgColumn ? (['organization'] as const) : []),
    'campaign',
    'tonnage',
    'real_yield',
    'area',
    'identified_area',
    'status',
  ];

  const tableOptions: MRT_TableOptions<SeedBatchRow> = {
    data: rows,
    columns,
    enableColumnFilters: false,
    enableExpanding: true,
    enableFullScreenToggle: false,
    enableRowActions: false,
    initialState: {
      expanded: true,
      pagination: { pageIndex: 0, pageSize: 25 },
    },

    displayColumnDefOptions: {
      'mrt-row-expand': {
        grow: false,
        maxSize: 44,
        minSize: 32,
        size: 40,
      },
    },

    mantineTableBodyCellProps: {
      style: {
        paddingInline: '6px',
      },
    },

    mantineTableHeadCellProps: {
      style: {
        fontSize: '12px',
        lineHeight: 1.25,
        paddingInline: '6px',
        verticalAlign: 'bottom',
        whiteSpace: 'nowrap',
      },
    },

    mantineTableContainerProps: {
      style: {
        overflowX: 'auto',
      },
    },

    mantineTableProps: {
      layout: 'fixed',
      style: {
        width: '100%',
        minWidth: 960,
        tableLayout: 'fixed',
      },
      verticalSpacing: 'sm',
    },
    state: {
      columnOrder,
    },
    getRowId: (row, index) => {
      const idPart = typeof row.id === 'string' ? row.id : String(row.id);

      return isSaipolSubRow(row) ? `${idPart}` : `batch-${idPart}-${index}`;
    },

    mantinePaperProps: {
      h: '100%',
      miw: 0,
      w: '100%',
    },

    mantinePaginationProps: {
      showRowsPerPage: false,
    },
  };

  return (
    <PfTable<SeedBatchRow>
      enableShowHideColumns={false}
      options={tableOptions}
    />
  );
};

export default SeedBatchesTable;
